import { withoutBlankFilterChoices } from "@/components/non_registrant/dashboard-column-filter-match";
import type { ColumnProperties } from "@/components/non_registrant/dashboard-table";
import { DashboardTable } from "@/components/non_registrant/dashboard-table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import LoadingScreen from "@/components/ui/loading-screen";
import { toast } from "@/components/ui/sonner";
import { UserType } from "@/constants";
import {
  getAdminUsersOptions,
  updateAdminUserMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import type { AdminUserResponseSchema } from "@/hera-api/client/types.gen";
import { useListColumnFilterSync } from "@/hooks/use-sync-global-column-filters";
import { invalidateAdminUserQueries } from "@/lib/admin-user-query-invalidation";
import { governmentImpersonatePortalHref } from "@/lib/government-impersonate";
import type { NamedIdOption } from "@/lib/sync-global-column-filters";
import {
  assertUnreachable,
  compareDateStrings,
  formatDateTime,
  getQueryErrorHeading,
  getQueryErrorMessage,
} from "@/lib/utils";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Plus, RotateCcw, Trash2, Upload } from "lucide-react";
import { type ReactNode, useCallback, useState } from "react";
import ErrorComponent from "../ui/error";
import { JurisdictionFilterWarningDialog } from "../ui/jurisdiction-filter-warning-dialog";
import { GovernmentUserUploadSheet } from "./government-user-upload-sheet";
import { UserDetailsSheet } from "./user-details-sheet";

function operatorIsAdmin(
  users: AdminUserResponseSchema[],
  email: string,
): boolean {
  const normalized = email.trim().toLowerCase();
  if (!normalized) return false;
  return users.some(
    (row) =>
      row.user_type === UserType.OPERATOR &&
      !row.is_deleted &&
      row.is_admin &&
      row.email.toLowerCase() === normalized,
  );
}

// Full column set (e.g. Government Users tab – includes jurisdiction)
const USER_ACCESSOR_KEYS = [
  "id",
  "auth0_subject",
  "email",
  "name",
  "jurisdiction_name",
  "jurisdiction_suspended",
  "is_main_contact",
  "is_remittance_contact",
  "last_login_at",
  "last_updated_at",
  "active",
] as const;

// Hera Operators tab – no jurisdiction columns (operators are not jurisdiction-specific)
const HERA_OPERATORS_ACCESSOR_KEYS = [
  "id",
  "auth0_subject",
  "email",
  "name",
  "is_admin",
  "is_sales",
  "last_login_at",
  "last_updated_at",
  "active",
] as const;

type UserAccessorKeys =
  | (typeof USER_ACCESSOR_KEYS)[number]
  | (typeof HERA_OPERATORS_ACCESSOR_KEYS)[number];
type UserColumnProperties = ColumnProperties<
  AdminUserResponseSchema,
  UserAccessorKeys
>;

const USER_COLUMNS_WITHOUT_BLANK_FILTER = new Set<string>([
  "id",
  "auth0_subject",
  "email",
  "is_main_contact",
  "is_remittance_contact",
  "is_admin",
  "is_sales",
  "active",
]);

const buildUserColumnProperties = (
  accessorKey: UserAccessorKeys,
): UserColumnProperties => {
  switch (accessorKey) {
    case "id":
      return {
        displayName: "ID",
        defaultVisible: false,
        getValue: (row) => row.id,
      };
    case "auth0_subject":
      return {
        displayName: "Auth0 ID",
        defaultVisible: false,
        getValue: (row) => row.auth0_subject,
      };
    case "email":
      return {
        displayName: "Email",
        defaultVisible: true,
        getValue: (row) => row.email,
      };
    case "name":
      return {
        displayName: "Name",
        defaultVisible: true,
        getValue: (row) => row.name ?? "",
      };
    case "jurisdiction_name":
      return {
        displayName: "Jurisdiction",
        defaultVisible: true,
        getValue: (row) => row.jurisdiction?.name || "",
      };
    case "jurisdiction_suspended":
      return {
        displayName: "Jurisdiction Status",
        defaultVisible: true,
        getValue: (row) =>
          row.jurisdiction
            ? row.jurisdiction.is_deleted
              ? "Suspended"
              : "Active"
            : "",
      };
    case "is_main_contact":
      return {
        displayName: "Main Contact",
        defaultVisible: true,
        getValue: (row) => (row.is_main_contact ? "Yes" : "No"),
      };
    case "is_remittance_contact":
      return {
        displayName: "Remittance Contact",
        defaultVisible: true,
        getValue: (row) => (row.is_remittance_contact ? "Yes" : "No"),
      };
    case "is_admin":
      return {
        displayName: "Admin",
        defaultVisible: true,
        getValue: (row) => (row.is_admin ? "Yes" : "No"),
      };
    case "is_sales":
      return {
        displayName: "Sales",
        defaultVisible: true,
        getValue: (row) => (row.is_sales ? "Yes" : "No"),
      };
    case "last_login_at":
      return {
        displayName: "Last Login",
        defaultVisible: true,
        getValue: (row) =>
          row.last_login_at ? formatDateTime(row.last_login_at) : "",
        sortingFn: (rowA, rowB) =>
          compareDateStrings(
            rowA.original.last_login_at,
            rowB.original.last_login_at,
          ),
      };
    case "last_updated_at":
      return {
        displayName: "Updated At",
        defaultVisible: true,
        getValue: (row) =>
          row.last_updated_at ? formatDateTime(row.last_updated_at) : "",
        sortingFn: (rowA, rowB) =>
          compareDateStrings(
            rowA.original.last_updated_at,
            rowB.original.last_updated_at,
          ),
      };
    case "active":
      return {
        displayName: "Active",
        defaultVisible: true,
        getValue: (row) => (!row.is_deleted ? "Yes" : "No"),
      };

    default:
      assertUnreachable(accessorKey);
  }
};

const getUserColumnProperties = (
  accessorKey: UserAccessorKeys,
): UserColumnProperties =>
  withoutBlankFilterChoices(
    accessorKey,
    USER_COLUMNS_WITHOUT_BLANK_FILTER,
    buildUserColumnProperties(accessorKey),
  );

interface DashboardUsersProps {
  /** Portal context (operator vs government) for jurisdiction filtering and add-user warning. */
  userType: UserType;
  isAuthSetupComplete: boolean;
  selectedJurisdictions: string[];
  /** When set with a catalog, the Jurisdiction column stays aligned with the header filter. */
  onSelectedJurisdictionsChange?: (ids: string[]) => void;
  jurisdictionCatalog?: readonly NamedIdOption[];
  /** When set, only show users of this type (e.g. when tab is "Hera Operators" or "Government Users"). */
  filterByUserType?: UserType;
  /** Show Impersonate that opens the government portal as this user. Operator portal only. */
  showImpersonateButton?: boolean;
  /** Page chrome rendered inside the list scroller so it can scroll away. */
  leadingContent?: ReactNode;
}

export function DashboardUsers({
  userType,
  isAuthSetupComplete,
  selectedJurisdictions,
  onSelectedJurisdictionsChange,
  jurisdictionCatalog = [],
  filterByUserType,
  showImpersonateButton = false,
  leadingContent,
}: DashboardUsersProps) {
  const { user } = useAuth0();
  const currentEmail = user?.email?.trim().toLowerCase() ?? "";
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isUploadSheetOpen, setIsUploadSheetOpen] = useState(false);
  const [isWarningDialogOpen, setIsWarningDialogOpen] = useState(false);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(
    {},
  );
  const [userPendingDelete, setUserPendingDelete] =
    useState<AdminUserResponseSchema | null>(null);

  // Determine which query functions to use based on user type
  const isOperator = userType === UserType.OPERATOR;
  const isGovernment = userType === UserType.GOVERNMENT;
  const queryClient = useQueryClient();
  const updateUserMutation = useMutation({
    ...updateAdminUserMutation(),
  });

  const setUserDeleted = useCallback(
    async (userId: string, isDeleted: boolean) => {
      try {
        await updateUserMutation.mutateAsync({
          path: { user_id: userId },
          body: { is_deleted: isDeleted },
        });
        toast({
          title: "Success",
          description: isDeleted ? "User deleted." : "User restored.",
          variant: "secondary",
        });
        await invalidateAdminUserQueries(queryClient);
      } catch (error) {
        toast({
          title: "Error",
          description: getQueryErrorMessage(error),
          variant: "destructive",
        });
      }
    },
    [queryClient, updateUserMutation],
  );

  // Users query to get data for the dashboard
  const {
    data: usersData,
    isLoading: isUsersQueryLoading,
    dataUpdatedAt,
    refetch: refetchUsers,
    error: usersQueryError,
  } = useQuery({
    ...getAdminUsersOptions({
      query: {
        include_deleted: true,
      },
    }),
    enabled: isAuthSetupComplete,
  });

  // Handle row click
  const handleRowClick = useCallback(
    (userId: string) => {
      const row = usersData?.users.find((item) => item.id === userId);
      if (
        row?.user_type === UserType.OPERATOR &&
        !operatorIsAdmin(usersData?.users ?? [], currentEmail)
      ) {
        return;
      }
      setSelectedUserId(userId);
      setIsSheetOpen(true);
    },
    [currentEmail, usersData?.users],
  );

  // Handle add new user
  const handleAddNewUser = useCallback(() => {
    // For government users, we don't need the warning dialog since they don't filter by jurisdictions
    if (isGovernment) {
      openNewUserSheet();
    } else {
      // Check if there's a filter applied (operator users only)
      if (selectedJurisdictions.length > 0) {
        setIsWarningDialogOpen(true);
      } else {
        openNewUserSheet();
      }
    }
  }, [selectedJurisdictions.length, isGovernment]);

  const openNewUserSheet = useCallback(() => {
    setSelectedUserId(null);
    setIsSheetOpen(true);
  }, []);

  // Only a refresh the user asked for greys the rows; writes and window-focus
  // refetches replace content that is already usable.
  const handleRefresh = useCallback(() => {
    setIsManualRefreshing(true);
    void refetchUsers().finally(() => setIsManualRefreshing(false));
  }, [refetchUsers]);

  const showJurisdictionColumn = filterByUserType !== UserType.OPERATOR;
  useListColumnFilterSync({
    columnFilters,
    setColumnFilters,
    jurisdictionColumnId: showJurisdictionColumn
      ? "jurisdiction_name"
      : undefined,
    selectedJurisdictions,
    setSelectedJurisdictions: onSelectedJurisdictionsChange,
    jurisdictions: jurisdictionCatalog,
  });

  if (isUsersQueryLoading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {leadingContent}
        <div className="flex justify-center items-center py-8">
          <LoadingScreen message="Fetching users..." />
        </div>
      </div>
    );
  }
  if (usersQueryError) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {leadingContent}
        <ErrorComponent
          title={getQueryErrorHeading(usersQueryError)}
          message={getQueryErrorMessage(usersQueryError)}
        />
      </div>
    );
  }

  // Filter by user type when a single-type tab is selected (e.g. Hera Operators / Government Users)
  let users = usersData?.users ?? [];
  if (filterByUserType != null) {
    users = users.filter((u) => u.user_type === filterByUserType);
  }

  // Jurisdiction filters apply to government users. Operators are not tied to a
  // jurisdiction, so that filter would hide the whole Hera Operators list.
  const availableUsers =
    isGovernment || filterByUserType === UserType.OPERATOR
      ? users
      : selectedJurisdictions.length > 0
        ? users.filter((u) =>
            selectedJurisdictions.includes(u.jurisdiction?.id ?? ""),
          )
        : users;

  const canManageOperators =
    isOperator && operatorIsAdmin(usersData?.users ?? [], currentEmail);
  const showAddUser =
    filterByUserType !== UserType.OPERATOR || canManageOperators;

  // Hera Operators tab: hide jurisdiction columns; Government Users tab: show all
  const accessorKeys =
    filterByUserType === UserType.OPERATOR
      ? HERA_OPERATORS_ACCESSOR_KEYS
      : USER_ACCESSOR_KEYS;

  const listChrome = (
    <div className="space-y-4 pb-4">
      {leadingContent}
      <div className="flex justify-end items-center gap-2">
        {isOperator && filterByUserType === UserType.GOVERNMENT ? (
          <Button onClick={() => setIsUploadSheetOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Upload Government Users
          </Button>
        ) : null}
        {showAddUser ? (
          <Button onClick={handleAddNewUser}>
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 min-w-0 overflow-hidden">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <DashboardTable<AdminUserResponseSchema, UserAccessorKeys>
          leadingContent={listChrome}
          lockToViewport
          data={availableUsers}
          onRowClick={handleRowClick}
          accessorKeys={accessorKeys}
          getColumnProperties={getUserColumnProperties}
          getRowId={(row) => row.id}
          tablePersistenceKey={
            filterByUserType === UserType.OPERATOR
              ? "non-registrant-users-operators"
              : "non-registrant-users"
          }
          initialSortColumn="last_updated_at"
          initialSortDirection="desc"
          columnFilters={columnFilters}
          defaultColumnFilters={{ active: ["Yes"] }}
          onColumnFiltersChange={setColumnFilters}
          hideTopPagination
          onRefresh={handleRefresh}
          isRefreshing={isManualRefreshing}
          refreshAriaLabel="Refresh users"
          dataUpdatedAt={dataUpdatedAt}
          rowActionsColumnWidth={showImpersonateButton ? 200 : 56}
          renderRowActions={
            filterByUserType === UserType.OPERATOR
              ? undefined
              : (row) =>
                  row.user_type === UserType.GOVERNMENT ? (
                    <div
                      className="flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      {showImpersonateButton ? (
                        <Button
                          size="xs"
                          variant="outline"
                          className="shrink-0"
                          aria-label="Impersonate government user"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(
                              governmentImpersonatePortalHref(row.id),
                              "_blank",
                            );
                          }}
                        >
                          <ExternalLink className="h-3 w-3 mr-1.5" />
                          Impersonate
                        </Button>
                      ) : null}
                      {row.is_deleted ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Restore government user"
                          title="Restore"
                          disabled={updateUserMutation.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            void setUserDeleted(row.id, false);
                          }}
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Delete government user"
                          title="Delete"
                          disabled={updateUserMutation.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            setUserPendingDelete(row);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ) : null
          }
        />
      </div>

      <AlertDialog
        open={userPendingDelete != null}
        onOpenChange={(open) => {
          if (!open) setUserPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete government user?</AlertDialogTitle>
            <AlertDialogDescription>
              {userPendingDelete
                ? `${userPendingDelete.email} will be deactivated and will no longer be able to sign in.`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                const userId = userPendingDelete?.id;
                setUserPendingDelete(null);
                if (userId) {
                  void setUserDeleted(userId, true);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UserDetailsSheet
        isOpen={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        userId={selectedUserId}
        key={selectedUserId}
        showImpersonateButton={showImpersonateButton}
        canManageOperators={canManageOperators}
      />

      {isOperator && filterByUserType === UserType.GOVERNMENT ? (
        <GovernmentUserUploadSheet
          isOpen={isUploadSheetOpen}
          onOpenChange={setIsUploadSheetOpen}
        />
      ) : null}

      {/* Only show warning dialog for operator users */}
      {isOperator && (
        <JurisdictionFilterWarningDialog
          isOpen={isWarningDialogOpen}
          onOpenChange={setIsWarningDialogOpen}
          onConfirm={() => {
            setIsWarningDialogOpen(false);
            openNewUserSheet();
          }}
          selectedJurisdictionsCount={selectedJurisdictions.length}
          recordType="user"
        />
      )}
    </div>
  );
}
